import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  updateDoc,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { CollegeAccount, CollegeStudent, normalizeAccount } from '../types';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [account, setAccount] = useState<CollegeAccount | null>(null);
  const [students, setStudents] = useState<CollegeStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [domainActivated, setDomainActivated] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ added: number; skipped: number } | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Gate on both user (admin confirmed) and id being present.
  // This prevents the Firestore read from firing before auth is ready.
  useEffect(() => {
    if (!user || !id) return;
    loadData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const loadData = async (accountId: string) => {
    setLoading(true);
    setError('');
    try {
      const [snap, studentsSnap, domainSnap] = await Promise.all([
        getDoc(doc(db, 'collegeAccounts', accountId)),
        getDocs(query(collection(db, 'collegeStudents'), where('accountId', '==', accountId))),
        getDocs(query(collection(db, 'profiles'), where('premiumAccountId', '==', accountId))),
      ]);
      if (!snap.exists()) {
        navigate('/dashboard');
        return;
      }
      setAccount(normalizeAccount(snap.id, snap.data()));
      setStudents(studentsSnap.docs.map((d) => d.data() as CollegeStudent));
      setDomainActivated(domainSnap.size);
    } catch (err: any) {
      if (err?.code === 'permission-denied' && !user) return;
      setError(err?.message || 'Failed to load account.');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id || !account) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const text = await file.text();
      const rows = parseCSV(text);

      // Build set of existing emails for this account to skip duplicates
      const existingEmails = new Set(students.map((s) => s.email.toLowerCase()));

      const newRows = rows.filter((r) => {
        const e = r.email.toLowerCase().trim();
        return e && e.includes('@') && !existingEmails.has(e);
      });

      let added = 0;
      // Firestore batch limit is 500 writes
      for (let i = 0; i < newRows.length; i += 499) {
        const chunk = newRows.slice(i, i + 499);
        const batch = writeBatch(db);
        for (const { email, name, graduationYear } of chunk) {
          const emailKey = email.toLowerCase().trim();
          batch.set(doc(db, 'collegeStudents', emailKey), {
            email: emailKey,
            name: name.trim(),
            graduationYear: graduationYear.trim() || null,
            accountId: id,
            accountName: account.name,
            activated: false,
            addedAt: serverTimestamp(),
          });
          added++;
        }
        await batch.commit();
      }

      const skipped = rows.length - added;
      setUploadResult({ added, skipped });
      await loadData(id);
    } catch (err: any) {
      alert('CSV upload failed: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeactivate = async () => {
    if (!id || !account) return;
    if (
      !window.confirm(
        `Deactivate "${account.name}"? The account will be marked inactive but all data will be preserved.`
      )
    )
      return;

    setDeactivating(true);
    await updateDoc(doc(db, 'collegeAccounts', id), {
      active: false,
      updatedAt: serverTimestamp(),
    });
    setAccount((prev) => (prev ? { ...prev, active: false } : null));
    setDeactivating(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          ← All Colleges
        </button>
        <div className="error-message" style={{ marginTop: 8 }}>{error}</div>
      </Layout>
    );
  }

  if (!account) return null;

  const csvWhitelisted = students.length;
  const csvActivated = students.filter((s) => s.activated).length;
  const activatedStudents = students
    .filter((s) => s.activated)
    .sort((a, b) => {
      const at = a.activatedAt ? (a.activatedAt as Timestamp).toMillis() : 0;
      const bt = b.activatedAt ? (b.activatedAt as Timestamp).toMillis() : 0;
      return bt - at;
    });

  return (
    <Layout>
      <div style={{ maxWidth: 960 }}>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          ← All Colleges
        </button>

        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{account.name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <span className={`badge badge-${account.type}`}>
                {account.type === 'college' ? 'College' : 'Alumni Association'}
              </span>
              <span className={`badge badge-${account.active ? 'active' : 'inactive'}`}>
                {account.active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/accounts/${account.id}/edit`)}
            >
              Edit
            </button>
            {account.active && (
              <button className="btn-danger" onClick={handleDeactivate} disabled={deactivating}>
                {deactivating ? 'Deactivating...' : 'Deactivate'}
              </button>
            )}
          </div>
        </div>

        {/* Detail cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div className="card">
            <div className="stat-label" style={{ marginBottom: 16 }}>Account Details</div>
            <Row label="Plan" value={cap(account.plan)} />
            <Row label={account.type === 'college' ? 'Student Limit' : 'Member Limit'} value={account.studentLimit?.toLocaleString()} />
            <Row
              label="Domain"
              value={
                account.domainEnabled
                  ? `Enabled — ${account.domain || 'no domain set'}`
                  : 'Disabled'
              }
            />
            <Row label="CSV Upload" value={account.csvEnabled ? 'Enabled' : 'Disabled'} />
            <Row label="Start Date" value={fmtDate(account.startDate)} />
            <Row label="Expiry Date" value={fmtDate(account.expiryDate)} />
            {account.website && (
              <Row
                label="Website"
                value={
                  <a href={account.website} target="_blank" rel="noreferrer">
                    {account.website}
                  </a>
                }
              />
            )}
            {account.city && (
              <Row label="Location" value={`${account.city}, ${account.state}`} />
            )}
          </div>

          <div className="card">
            <div className="stat-label" style={{ marginBottom: 16 }}>Contact</div>
            {account.contactName ? (
              <Row label="Name" value={account.contactName} />
            ) : null}
            {account.contactEmail ? (
              <Row
                label="Email"
                value={
                  <a href={`mailto:${account.contactEmail}`}>{account.contactEmail}</a>
                }
              />
            ) : null}
            {account.contactPhone ? (
              <Row label="Phone" value={account.contactPhone} />
            ) : null}
            {!account.contactName && !account.contactEmail && !account.contactPhone && (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No contact info</p>
            )}
            {account.notes && (
              <>
                <div
                  style={{ borderTop: '1px solid var(--border)', margin: '16px 0' }}
                />
                <div className="stat-label" style={{ marginBottom: 10 }}>Notes</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7 }}>
                  {account.notes}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Activation stats */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div className="stat-card">
            <div className="stat-label">CSV Whitelisted</div>
            <div className="stat-value">{csvWhitelisted}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">CSV Activated</div>
            <div className="stat-value" style={{ color: 'var(--accent-text)' }}>
              {csvActivated}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Domain Activated</div>
            <div className="stat-value" style={{ color: 'var(--accent-text)' }}>
              {domainActivated}
            </div>
          </div>
        </div>

        {/* CSV Upload */}
        {account.csvEnabled && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>CSV Upload</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Upload a CSV with columns: <code>email</code>, <code>name</code>, <code>graduationYear</code> —
              name and graduationYear are optional. An optional header row is ignored. Existing emails are skipped.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleCSVUpload}
              style={{ display: 'none' }}
            />

            <button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </button>

            {uploadResult && (
              <div className="success-message" style={{ marginTop: 14 }}>
                Added <strong>{uploadResult.added}</strong> new email
                {uploadResult.added !== 1 ? 's' : ''}.
                {uploadResult.skipped > 0 &&
                  ` ${uploadResult.skipped} skipped (duplicates or invalid).`}
              </div>
            )}
          </div>
        )}

        {/* Activated members list */}
        {activatedStudents.length > 0 && (
          <div className="card" style={{ padding: 0 }}>
            <div
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h2 style={{ fontSize: 14, fontWeight: 600 }}>
                Activated {account.type === 'college' ? 'Students' : 'Members'} ({activatedStudents.length})
              </h2>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Activated</th>
                  </tr>
                </thead>
                <tbody>
                  {activatedStudents.map((s) => (
                    <tr key={s.email}>
                      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{s.email}</td>
                      <td style={{ color: s.name ? undefined : 'var(--text-muted)' }}>
                        {s.name || '—'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {fmtTimestamp(s.activatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 10,
        gap: 16,
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: 'right' }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function fmtTimestamp(ts?: Timestamp) {
  if (!ts) return '—';
  return ts.toDate().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function parseCSV(text: string): { email: string; name: string; graduationYear: string }[] {
  const results: { email: string; name: string; graduationYear: string }[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());

  for (const line of lines) {
    const parts = line
      .split(',')
      .map((p) => p.trim().replace(/^"(.*)"$/, '$1').trim());

    if (parts.length < 1) continue;
    const [first, second = '', third = ''] = parts;
    if (first.toLowerCase() === 'email') continue; // header row
    if (first.includes('@')) {
      results.push({ email: first, name: second, graduationYear: third });
    }
  }

  return results;
}
